import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import Logo from '@/components/Logo';

// Photos du bar utilisées comme image héros à gauche du formulaire.
// Une est tirée au sort à chaque visite de la page de connexion.
const HERO_IMAGES = [
  '/images/marketing/alambic-tropical.webp',
  '/images/marketing/aperol-spritz.webp',
  '/images/marketing/cocktail-framboise.webp',
  '/images/marketing/gins.webp',
  '/images/marketing/plateau-gins.webp',
  '/images/marketing/plateau-fromage.webp',
  '/images/marketing/whisky-sour.webp',
] as const;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Lazy initializer : tirage au sort UNE SEULE fois au mount, pas à chaque re-render
  const [heroImage] = useState(
    () => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)]
  );
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: signInError } = await signIn(email, password);

    if (signInError) {
      setError(signInError.message);
      setLoading(false);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground lg:grid lg:grid-cols-2">
      {/* ─── Colonne gauche : image immersive du bar (cachée < lg) ─── */}
      <div className="relative hidden lg:block">
        <img
          src={heroImage}
          alt="Bar L'Alambic — ambiance"
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        {/* Overlay sombre dégradé pour lisibilité du texte par-dessus */}
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-background/20" />

        {/* Logo + tagline en bas-gauche par-dessus l'image */}
        <div className="relative z-10 flex h-full flex-col justify-between p-10">
          <Link
            to="/"
            className="inline-flex items-center gap-3 self-start"
            title="Retour au site public"
          >
            <Logo size="h-12 shrink-0" />
            <span className="font-display text-2xl font-bold text-primary">
              Bar L'Alambic
            </span>
          </Link>

          <blockquote className="space-y-2">
            <p className="font-display text-2xl text-foreground/90 leading-relaxed">
              « L'univers de la dégustation. »
            </p>
            <footer className="text-xs text-muted-foreground tracking-[0.2em] uppercase">
              La Sarre, Abitibi-Témiscamingue
            </footer>
          </blockquote>
        </div>
      </div>

      {/* ─── Colonne droite : formulaire de connexion ─── */}
      <div className="relative flex items-center justify-center px-4 py-8">
        {/* Lien retour vers le site vitrine — visible partout (mobile + desktop) */}
        <Link
          to="/"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Retour au site
        </Link>

        <div className="w-full max-w-sm">
          {/* Logo mobile uniquement (caché >=lg car visible dans la colonne image) */}
          <div className="lg:hidden flex flex-col items-center text-center mb-8">
            <Logo size="h-20" className="mb-4" />
            <h1 className="font-display text-2xl font-bold text-primary">
              Bar L'Alambic
            </h1>
            <p className="text-[10px] text-muted-foreground mt-2 tracking-[0.2em] uppercase">
              Système de gestion
            </p>
          </div>

          <div className="space-y-2 mb-6">
            <h2 className="font-display text-3xl font-semibold text-foreground">
              Connexion
            </h2>
            <p className="text-sm text-muted-foreground">
              Entre tes identifiants pour accéder au système de gestion.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wide text-muted-foreground">
                Courriel
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@example.com"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-muted-foreground">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground/70 mt-8">
            © 2026 Bar L'Alambic
          </p>
        </div>
      </div>
    </div>
  );
}
